import { SpatialMap, WorldPosition } from './protocol';

export interface MapRoom { room_id: string; name?: string; room_name?: string; spawn_position?: string; podium_position?: string }
export function readPosition(value?: string): WorldPosition | undefined {
  const parts = value?.replace(/[()]/g, '').split(',').map(Number);
  if (!parts || parts.length !== 3 || parts.some(n => !Number.isFinite(n))) return;
  return { x: parts[0], y: parts[1], z: parts[2] };
}
export function roomName(room: MapRoom): string { return room.name || room.room_name || room.room_id; }

export function prepareMap(map: SpatialMap) {
  const triangles: { points: WorldPosition[]; minY: number; maxY: number }[] = [];
  const edges = new Map<string, { a: WorldPosition; b: WorldPosition; count: number }>();
  const key = (p: WorldPosition) => [p.x, p.y, p.z].map(n => Math.round(n * 100)).join(',');
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (let i = 0; i < map.indices.length; i += 3) {
    const points = map.indices.slice(i, i + 3).map(index => map.vertices[index]);
    if (points.length !== 3 || points.some(p => !p || ![p.x, p.y, p.z].every(Number.isFinite))) continue;
    triangles.push({ points, minY: Math.min(...points.map(p => p.y)), maxY: Math.max(...points.map(p => p.y)) });
    points.forEach((a, index) => {
      minX = Math.min(minX, a.x); maxX = Math.max(maxX, a.x); minZ = Math.min(minZ, a.z); maxZ = Math.max(maxZ, a.z);
      const b = points[(index + 1) % 3]; const edgeKey = [key(a), key(b)].sort().join('|');
      const existing = edges.get(edgeKey);
      if (existing) existing.count++; else edges.set(edgeKey, { a, b, count: 1 });
    });
  }
  return { triangles, boundaries: Array.from(edges.values()).filter(edge => edge.count === 1),
    bounds: triangles.length ? { minX, maxX, minZ, maxZ } : undefined };
}

export type MapCamera = { x: number; z: number; y: number; scale: number; width: number; height: number };
/** Intersect's authored overhead view faces the opposite direction from canvas +X. */
export function projectMapX(worldX: number, camera: MapCamera): number {
  return camera.width / 2 - (worldX - camera.x) * camera.scale;
}
function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r); ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r); ctx.closePath();
}
export function drawMap(ctx: CanvasRenderingContext2D, geometry: ReturnType<typeof prepareMap>, camera: MapCamera, rooms: MapRoom[]) {
  const { width, height, scale, y } = camera;
  const sx = (n: number) => projectMapX(n, camera);
  const sy = (n: number) => height / 2 + (n - camera.z) * scale;
  const background = ctx.createLinearGradient(0, 0, width, height);
  background.addColorStop(0, '#cce4b7'); background.addColorStop(0.55, '#b8d7a3'); background.addColorStop(1, '#9fc88f');
  ctx.fillStyle = background; ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = 'rgba(52, 103, 67, 0.1)'; ctx.lineWidth = 1;
  const step = 5 * scale;
  ctx.beginPath();
  for (let x = ((width / 2 + camera.x * scale) % step + step) % step; x < width; x += step) { ctx.moveTo(x, 0); ctx.lineTo(x, height); }
  for (let z = ((height / 2 - camera.z * scale) % step + step) % step; z < height; z += step) { ctx.moveTo(0, z); ctx.lineTo(width, z); }
  ctx.stroke();
  const main = rooms.find(r => r.room_id === 'intersect-main');
  const center = readPosition(main?.spawn_position);
  for (let pass = 0; pass < 2; pass++) for (const triangle of geometry.triangles) {
    const current = y >= triangle.minY - 1.5 && y <= triangle.maxY + 1.5;
    if (Number(current) !== pass) continue;
    const [a, b, c] = triangle.points;
    if (Math.max(sx(a.x), sx(b.x), sx(c.x)) < 0 || Math.min(sx(a.x), sx(b.x), sx(c.x)) > width || Math.max(sy(a.z), sy(b.z), sy(c.z)) < 0 || Math.min(sy(a.z), sy(b.z), sy(c.z)) > height) continue;
    const ramp = triangle.maxY - triangle.minY > 0.35;
    const central = center && Math.hypot((a.x + b.x + c.x) / 3 - center.x, (a.z + b.z + c.z) / 3 - center.z) < 13;
    ctx.fillStyle = !current ? '#9eafb5' : ramp ? '#c99b50' : central ? '#80b9c8' : '#3977a8';
    ctx.beginPath(); ctx.moveTo(sx(a.x), sy(a.z)); ctx.lineTo(sx(b.x), sy(b.z)); ctx.lineTo(sx(c.x), sy(c.z)); ctx.closePath(); ctx.fill();
  }
  // Only outer mesh edges: do not expose the triangulation as visual clutter.
  ctx.lineWidth = 2.5; ctx.lineJoin = 'round'; ctx.strokeStyle = '#dbe9cf'; ctx.beginPath();
  for (const { a, b } of geometry.boundaries) {
    if (y < Math.min(a.y, b.y) - 1.5 || y > Math.max(a.y, b.y) + 1.5) continue;
    ctx.moveTo(sx(a.x), sy(a.z)); ctx.lineTo(sx(b.x), sy(b.z));
  }
  ctx.stroke();
  for (const room of rooms) {
    const pos = readPosition(room.spawn_position);
    if (!pos || Math.abs(pos.y - y) > 3) continue;
    const x = sx(pos.x), z = sy(pos.z);
    if (x < -100 || x > width + 100 || z < -80 || z > height + 80) continue;
    ctx.save(); ctx.shadowColor = 'rgba(14, 43, 64, 0.3)'; ctx.shadowBlur = 12;
    ctx.beginPath(); ctx.arc(x, z, Math.max(11, scale * 0.85), 0, Math.PI * 2);
    ctx.fillStyle = '#f7f4e8'; ctx.fill(); ctx.shadowBlur = 0; ctx.strokeStyle = '#4f8f72'; ctx.lineWidth = 3; ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x - 4, z - 5); ctx.lineTo(x + 5, z); ctx.lineTo(x - 4, z + 5); ctx.closePath();
    ctx.fillStyle = '#245d86'; ctx.fill(); ctx.restore();
    ctx.font = '600 12px system-ui'; ctx.textAlign = 'center';
    const label = roomName(room), labelWidth = ctx.measureText(label).width + 20;
    roundedRect(ctx, x - labelWidth / 2, z + 22, labelWidth, 25, 12);
    ctx.fillStyle = '#173651'; ctx.fill();
    ctx.fillStyle = '#fff6e4'; ctx.fillText(label, x, z + 39);
  }
}
